/**
 * <s-light-card> — Card that tilts toward the pointer and catches the light.
 *
 * Writes --rx/--ry (tilt) and --gx/--gy (glare position) on the element's
 * [data-light-card] child while the pointer moves over it, and sets
 * [data-visible] once the card scrolls into view so CSS can play a one-time sheen.
 * Respects prefers-reduced-motion.
 */
class SLightCard extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const card = this.querySelector('[data-light-card]');
    if (!card) return;

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.setAttribute('data-visible', '');
          this.#observer?.disconnect();
        }
      }, { threshold: 0.4 });
      this.#observer.observe(card);
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const reset = () => {
      card.removeAttribute('data-active');
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
      card.style.setProperty('--gx', '50%');
      card.style.setProperty('--gy', '30%');
    };

    card.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch') return;
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      card.setAttribute('data-active', '');
      card.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`);
      card.style.setProperty('--rx', `${((0.5 - y) * 10).toFixed(2)}deg`);
      card.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
      card.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
    }, opts);

    card.addEventListener('pointerleave', reset, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-light-card')) {
  customElements.define('s-light-card', SLightCard);
}
